const isZodType = (obj) => {
    return typeof (obj === null || obj === void 0 ? void 0 : obj.safeParse) === 'function';
};
const isZodObject = (obj) => {
    if (typeof (obj === null || obj === void 0 ? void 0 : obj.passthrough) === 'function') {
        return true;
    }
    if (typeof (obj === null || obj === void 0 ? void 0 : obj.innerType) === 'function') {
        return isZodObject(obj === null || obj === void 0 ? void 0 : obj.innerType());
    }
    return false;
};
const isZodObjectStrict = (obj) => {
    return typeof (obj === null || obj === void 0 ? void 0 : obj.passthrough) === 'function';
};
const extractZodObjectShape = (obj) => {
    if (!isZodObject(obj)) {
        throw new Error('Unknown zod object type');
    }
    if ('innerType' in obj) {
        return extractZodObjectShape(obj.innerType());
    }
    return obj.shape;
};
const zodMerge = (objectA, objectB) => {
    if (isZodObjectStrict(objectA)) {
        if (isZodObjectStrict(objectB)) {
            return objectA.merge(objectB);
        }
        return objectA;
    }
    if (isZodObjectStrict(objectB)) {
        return objectB;
    }
    return Object.assign({}, objectA, objectB);
};
const checkZodSchema = (data, schema, { passThroughExtraKeys = false } = {}) => {
    if (isZodType(schema)) {
        const result = schema.safeParse(data);
        if (result.success) {
            return {
                success: true,
                data: passThroughExtraKeys && typeof data === 'object'
                    ? { ...data, ...result.data }
                    : result.data,
            };
        }
        return {
            success: false,
            error: result.error,
        };
    }
    return {
        success: true,
        data: data,
    };
};
const zodErrorResponse = (error) => {
    return {
        name: error.name,
        issues: error.issues,
    };
};

const isAppRoute = (obj) => {
    return 'method' in obj && 'path' in obj;
};
const initTsRest = () => initContract();
const recursivelyApplyOptions = (router, options) => {
    return Object.fromEntries(Object.entries(router).map(([key, value]) => {
        var _a, _b;
        if (isAppRoute(value)) {
            return [
                key,
                {
                    ...value,
                    path: (options === null || options === void 0 ? void 0 : options.pathPrefix)
                        ? options.pathPrefix + value.path
                        : value.path,
                    headers: zodMerge(options === null || options === void 0 ? void 0 : options.baseHeaders, value.headers),
                    strictStatusCodes: (_a = value.strictStatusCodes) !== null && _a !== void 0 ? _a : options === null || options === void 0 ? void 0 : options.strictStatusCodes,
                    validateResponseOnClient: (_b = value.validateResponseOnClient) !== null && _b !== void 0 ? _b : options === null || options === void 0 ? void 0 : options.validateResponseOnClient
                },
            ];
        }
        else {
            return [key, recursivelyApplyOptions(value, options)];
        }
    }));
};
const ContractPlainTypeRuntimeSymbol = Symbol('ContractPlainType');
const initContract = () => {
    return {
        router: (endpoints, options) => recursivelyApplyOptions(endpoints, options),
        query: (args) => args,
        mutation: (args) => args,
        response: () => ContractPlainTypeRuntimeSymbol,
        body: () => ContractPlainTypeRuntimeSymbol,
        type: () => ContractPlainTypeRuntimeSymbol,
        otherResponse: ({ contentType, body, }) => ({
            contentType,
            body,
        }),
    };
};

const insertParamsIntoPath = ({ path, params, }) => {
    return path
        .replace(/:([^/]+)/g, (_, p) => {
        return params[p] || '';
    })
        .replace(/\/\//g, '/');
};

const convertQueryParamsToUrlString = (query, json = false) => {
    const queryString = json
        ? encodeQueryParamsJson(query)
        : encodeQueryParams(query);
    return (queryString === null || queryString === void 0 ? void 0 : queryString.length) > 0 ? '?' + queryString : '';
};
const encodeQueryParamsJson = (query) => {
    if (!query) {
        return '';
    }
    return Object.entries(query)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => {
        let encodedValue;
        if (typeof value === 'string' &&
            !['true', 'false', 'null'].includes(value.trim()) &&
            isNaN(Number(value))) {
            encodedValue = value;
        }
        else {
            encodedValue = JSON.stringify(value);
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(encodedValue)}`;
    })
        .join('&');
};
const encodeQueryParams = (query) => {
    if (!query) {
        return '';
    }
    return (Object.keys(query)
        .flatMap((key) => tokeniseValue(key, query[key]))
        .map(([key, value]) => {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
        .join('&'));
};
const tokeniseValue = (key, value) => {
    if (Array.isArray(value)) {
        return value.flatMap((v, idx) => tokeniseValue(`${key}[${idx}]`, v));
    }
    if (value instanceof Date) {
        return [[`${key}`, value.toISOString()]];
    }
    if (value === null) {
        return [[`${key}`, '']];
    }
    if (value === undefined) {
        return [];
    }
    if (typeof value === 'object') {
        return Object.keys(value).flatMap((k) =>
        tokeniseValue(`${key}[${k}]`, value[k]));
    }
    return [[`${key}`, `${value}`]];
};
const parseJsonQueryObject = (query) => {
    return Object.fromEntries(Object.entries(query).map(([key, value]) => {
        let parsedValue;
        try {
            parsedValue = JSON.parse(value);
        }
        catch {
            parsedValue = value;
        }
        return [key, parsedValue];
    }));
};

class UnknownStatusError extends Error {
    constructor(response, knownResponseStatuses) {
        const expectedStatuses = knownResponseStatuses.join(',');
        super(`Server returned unexpected response. Expected one of: ${expectedStatuses} got: ${response.status}`);
        this.response = response;
    }
}

function getRouteResponses(router) {
    return {};
}
const tsRestFetchApi = async ({ path, method, headers, body, credentials, signal, cache, next, route, }) => {
    const result = await fetch(path, {
        method,
        headers,
        body,
        credentials,
        signal,
        cache,
        next,
    });
    const contentType = result.headers.get('content-type');
    if ((contentType === null || contentType === void 0 ? void 0 : contentType.includes('application/')) && (contentType === null || contentType === void 0 ? void 0 : contentType.includes('json'))) {
        if (!route.validateResponseOnClient) {
            return {
                status: result.status,
                body: await result.json(),
                headers: result.headers,
            };
        }
        const jsonData = await result.json();
        const statusCode = result.status;
        const response = route.responses[statusCode];
        return {
            status: statusCode,
            body: response && typeof response !== 'symbol' && 'parse' in response
                ? response === null || response === void 0 ? void 0 : response.parse(jsonData)
                : jsonData,
            headers: result.headers,
        };
    }
    if (contentType === null || contentType === void 0 ? void 0 : contentType.includes('text/')) {
        return {
            status: result.status,
            body: await result.text(),
            headers: result.headers,
        };
    }
    return {
        status: result.status,
        body: await result.blob(),
        headers: result.headers,
    };
};
const createFormData = (body) => {
    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) => {
        if (value instanceof File) {
            formData.append(key, value);
        }
        else {
            formData.append(key, JSON.stringify(value));
        }
    });
    return formData;
};
const normalizeHeaders = (headers) => {
    return Object.fromEntries(Object.entries(headers).map(([k, v]) => [k.toLowerCase(), v]));
};
const fetchApi = ({ path, clientArgs, route, body, query, extraInputArgs, headers, signal, next, }) => {
    const apiFetcher = clientArgs.api || tsRestFetchApi;
    const combinedHeaders = {
        ...normalizeHeaders(clientArgs.baseHeaders),
        ...normalizeHeaders(headers),
    };
    Object.keys(combinedHeaders).forEach((key) => {
        if (combinedHeaders[key] === undefined) {
            delete combinedHeaders[key];
        }
    });
    if (route.method !== 'GET' && route.contentType === 'multipart/form-data') {
        return apiFetcher({
            route,
            path,
            method: route.method,
            credentials: clientArgs.credentials,
            headers: combinedHeaders,
            body: body instanceof FormData ? body : createFormData(body),
            rawBody: body,
            rawQuery: query,
            contentType: 'multipart/form-data',
            signal,
            next,
            ...extraInputArgs,
        });
    }
    const includeContentTypeHeader = route.method !== 'GET' && body !== null && body !== undefined;
    return apiFetcher({
        route,
        path,
        method: route.method,
        credentials: clientArgs.credentials,
        headers: {
            ...(includeContentTypeHeader && { 'content-type': 'application/json' }),
            ...combinedHeaders,
        },
        body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
        rawBody: body,
        rawQuery: query,
        contentType: includeContentTypeHeader ? 'application/json' : undefined,
        signal,
        next,
        ...extraInputArgs,
    });
};
const getCompleteUrl = (query, baseUrl, params, route, jsonQuery) => {
    const path = insertParamsIntoPath({
        path: route.path,
        params: params,
    });
    const queryComponent = convertQueryParamsToUrlString(query, jsonQuery);
    return `${baseUrl}${path}${queryComponent}`;
};
const getRouteQuery = (route, clientArgs) => {
    const knownResponseStatuses = Object.keys(route.responses);
    return async (inputArgs) => {
        const { query, params, body, headers, extraHeaders, next, ...extraInputArgs } =
        inputArgs || {};
        const completeUrl = getCompleteUrl(query, clientArgs.baseUrl, params, route, !!clientArgs.jsonQuery);
        const response = await fetchApi({
            path: completeUrl,
            clientArgs,
            route,
            body,
            query,
            extraInputArgs,
            next,
            headers: {
                ...extraHeaders,
                ...headers,
            },
        });
        if (!clientArgs.throwOnUnknownStatus) {
            return response;
        }
        if (knownResponseStatuses.includes(response.status.toString())) {
            return response;
        }
        throw new UnknownStatusError(response, knownResponseStatuses);
    };
};
const initClient = (router, args) => {
    return Object.fromEntries(Object.entries(router).map(([key, subRouter]) => {
        if (isAppRoute(subRouter)) {
            return [key, getRouteQuery(subRouter, args)];
        }
        else {
            return [key, initClient(subRouter, args)];
        }
    }));
};

class ResponseValidationError extends Error {
    constructor(appRoute, cause) {
        super(`[ts-rest] Response validation failed for ${appRoute.method} ${appRoute.path}: ${cause.message}`);
        this.appRoute = appRoute;
        this.cause = cause;
    }
}

const isAppRouteResponse = (value) => {
    return (value != null &&
        typeof value === 'object' &&
        'status' in value &&
        typeof value.status === 'number');
};
const isAppRouteOtherResponse = (response) => {
    return (response != null &&
        typeof response === 'object' &&
        'contentType' in response);
};
const validateResponse = ({ appRoute, response, }) => {
    if (isAppRouteResponse(response)) {
        const responseType = appRoute.responses[response.status];
        const responseSchema = isAppRouteOtherResponse(responseType)
            ? responseType.body
            : responseType;
        const responseValidation = checkZodSchema(response.body, responseSchema);
        if (!responseValidation.success) {
            throw new ResponseValidationError(appRoute, responseValidation.error);
        }
        return {
            status: response.status,
            body: responseValidation.data,
        };
    }
    return response;
};

export { ContractPlainTypeRuntimeSymbol, ResponseValidationError, UnknownStatusError, checkZodSchema, convertQueryParamsToUrlString, encodeQueryParams, encodeQueryParamsJson, extractZodObjectShape, fetchApi, getCompleteUrl, getRouteQuery, getRouteResponses, initClient, initContract, initTsRest, insertParamsIntoPath, isAppRoute, isAppRouteOtherResponse, isAppRouteResponse, isZodObject, isZodObjectStrict, isZodType, parseJsonQueryObject, tsRestFetchApi, validateResponse, zodErrorResponse, zodMerge };
